import { where, orderBy, limit as firestoreLimit, Timestamp } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/config";
import { getDocuments } from "../../firebase/firestore";
import { RecentFits, RecentFitsData } from "../../types";

export type TimeFilter = "week" | "month" | "year" | "all";

export interface RecentFitsWithImages {
  fits: RecentFits[];
  images: Record<string, string>;
}

/**
 * Get the filter date based on the time filter
 */
export const getFilterDate = (filter: TimeFilter): Date | null => {
  if (filter === "all") {
    return null;
  }

  const now = new Date();
  switch (filter) {
    case "week":
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case "month":
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    case "year":
      return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
    default:
      return null;
  }
};

/**
 * Get the display label for a time filter
 */
export const getFilterLabel = (filter: TimeFilter): string => {
  switch (filter) {
    case "week":
      return "Last Week";
    case "month":
      return "Last Month";
    case "year":
      return "Last Year";
    case "all":
      return "All Time";
    default:
      return "All Time";
  }
};

/**
 * Load recent fits for a user with optional time filtering
 */
export const loadRecentFits = async (
  userId: string,
  filter: TimeFilter = "all",
  maxResults: number = 50,
): Promise<RecentFits[]> => {
  try {
    const filterDate = getFilterDate(filter);
    const constraints = [
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      firestoreLimit(maxResults),
    ];

    // Add date filter if specified
    if (filterDate) {
      constraints.splice(1, 0, where("createdAt", ">=", Timestamp.fromDate(filterDate)));
    }

    const fitsData = await getDocuments<RecentFitsData>("recentfits", constraints);

    // Convert Firestore timestamps to Date objects
    return fitsData.map(fit => ({
      ...fit,
      createdAt: fit.createdAt instanceof Date ? fit.createdAt : fit.createdAt.toDate(),
      updatedAt: fit.updatedAt instanceof Date ? fit.updatedAt : fit.updatedAt.toDate(),
    })) as RecentFits[];
  } catch (error) {
    console.error("Error loading recent fits:", error);
    throw error;
  }
};

/**
 * Load images for a list of fits from Firebase Storage
 */
export const loadFitImages = async (fits: RecentFits[]): Promise<Record<string, string>> => {
  try {
    const imageLoadPromises: Promise<void>[] = [];
    const images: Record<string, string> = {};

    fits.forEach((fit) => {
      const promise = async () => {
        try {
          const imageRef = ref(storage, fit.generatedImagePath);
          const imageUrl = await getDownloadURL(imageRef);
          images[fit.id] = imageUrl;
        } catch (error) {
          console.error(`Error loading fit image for ${fit.id}:`, error);
        }
      };
      imageLoadPromises.push(promise());
    });

    await Promise.all(imageLoadPromises);
    return images;
  } catch (error) {
    console.error("Error loading fit images:", error);
    throw error;
  }
};

/**
 * Load recent fits with their images
 */
export const loadRecentFitsWithImages = async (
  userId: string,
  filter: TimeFilter = "all",
  maxResults: number = 50,
): Promise<RecentFitsWithImages> => {
  try {
    const fits = await loadRecentFits(userId, filter, maxResults);
    const images = await loadFitImages(fits);

    return { fits, images };
  } catch (error) {
    console.error("Error loading recent fits with images:", error);
    throw error;
  }
};

/**
 * Get the count of fits for a specific time filter
 */
export const getFitCount = (fits: RecentFits[], filter: TimeFilter): number => {
  if (filter === "all") {
    return fits.length;
  }

  const filterDate = getFilterDate(filter);
  if (!filterDate) {
    return fits.length;
  }

  return fits.filter(fit => fit.createdAt >= filterDate).length;
};

/**
 * Open a shop link in a new tab safely
 */
export const openShopLink = (shopLink: string): void => {
  window.open(shopLink, "_blank", "noopener,noreferrer");
};
